'use client';

import React, { ChangeEvent, Fragment, useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from '@/hooks';
import Box from '@mui/material/Box';
import {
  LabelWrapper,
  StyledCheckbox,
  StyledCountData,
  StyledFormControl,
  StyledFormControlLabel,
} from '../../common/LandMultilevelCheckboxList/styles';
import { UnitsMultilevelList } from '@/types';
import { FilterAccordion } from '@/components';
import { Button, Divider, FormControl, InputLabel, Select } from '@mui/material';
import { COLORS, COUNTRIES_ORDER } from '@/constants';
import { ArrowDown } from '@/components/icons/ArrowDown';
import { useTranslations } from 'next-intl';

interface LandMultilevelCheckboxListProps {
  label: string;
  unitListValue: string[];
  setUnitListValue: (value: string[]) => void;
  unitList?: UnitsMultilevelList;
  isSelect?: boolean;
  width?: string | number;
}

type UnitNode = {
  code: string;
  title: string;
  count?: number;
  children?: UnitNode[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
};

export const LandMultilevelCheckboxList: React.FC<LandMultilevelCheckboxListProps> = ({
  label,
  unitList,
  unitListValue,
  setUnitListValue,
  isSelect = false,
  width = '200px',
}) => {
  const t = useTranslations();

  const [selectOpen, setSelectOpen] = useState(false);
  const [showZero, setShowZero] = useLocalStorage<boolean>('filter:showEmpty:land', false);
  const [showOtherCountries, setShowOtherCountries] = useLocalStorage<boolean>(
    'filter:showOtherCountries',
    false,
  );

  const order = COUNTRIES_ORDER;

  /** First country in filter order (AT) — default expanded when selection is empty (same as mobile). */
  const defaultExpandedRootCode = useMemo(() => order?.flat()?.[0] ?? null, [order]);

  const hasNonZeroInSubtree = useCallback((node: UnitNode): boolean => {
    if ((node.count ?? 0) > 0) return true;
    return (node.children || []).some(hasNonZeroInSubtree);
  }, []);

  const hasZeroInSubtree = useCallback((node: UnitNode): boolean => {
    if ((node.count ?? 0) === 0) return true;
    return (node.children || []).some(hasZeroInSubtree);
  }, []);

  const titleMap = useMemo(() => {
    const map = new Map<string, string>();
    const traverse = (nodes?: UnitsMultilevelList) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      nodes?.forEach((n: any) => {
        map.set(n.code, n.title);
        if (n.children) traverse(n.children);
      });
    };
    traverse(unitList);
    return map;
  }, [unitList]);

  const getTitleByCode = useCallback((code: string) => titleMap.get(code) ?? code, [titleMap]);

  const { nonZeroList, zeroList } = useMemo(() => {
    const nonZeroList: UnitNode[] = [];
    const zeroList: UnitNode[] = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const makeZeroCopy = (item: any): UnitNode => ({
      ...item,
      count: 0,
      children: (item.children || []).filter(hasZeroInSubtree).map(makeZeroCopy),
    });

    for (const land of unitList || []) {
      const nonZeroChildren = (land.children || []).filter(hasNonZeroInSubtree);
      const zeroChildren = (land.children || []).filter(hasZeroInSubtree).map(makeZeroCopy);

      const landHasNonZero = (land.count ?? 0) > 0 || nonZeroChildren.length > 0;
      const landHasZero =
        (land.count ?? 0) === 0 && (land.children || []).length === 0
          ? true
          : zeroChildren.length > 0;

      if (landHasNonZero) nonZeroList.push({ ...land, children: land.children || [] });
      if (landHasZero && !landHasNonZero)
        zeroList.push(makeZeroCopy({ ...land, children: zeroChildren }));
    }

    return { nonZeroList, zeroList };
  }, [unitList]);

  const groupedNonZero = useMemo(() => {
    if (!order?.length) return [nonZeroList];
    const flatOrder = order.flat();
    return [
      ...order.map((group) =>
        group
          .map((code) => nonZeroList.find((i) => i.code === code))
          .filter((i): i is UnitNode => Boolean(i)),
      ),
      nonZeroList.filter((i) => !flatOrder.includes(i.code)),
    ];
  }, [order, nonZeroList]);

  const groupedZero = useMemo(() => {
    if (!order?.length) return [zeroList];
    const flatOrder = order.flat();
    return [
      ...order.map((group) =>
        group
          .map((code) => zeroList.find((i) => i.code === code))
          .filter((i): i is UnitNode => Boolean(i)),
      ),
      zeroList.filter((i) => !flatOrder.includes(i.code)),
    ];
  }, [order, zeroList]);

  const handleChange = useCallback(
    (code: string) => (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const checked = event.target.checked;
      const current = [...unitListValue];
      const parts = code.split('.');

      if (checked) {
        if (!current.includes(code)) current.push(code);
        if (parts.length === 3) {
          const p2 = `${parts[0]}.${parts[1]}`;
          if (!current.includes(p2)) current.push(p2);
          if (!current.includes(parts[0])) current.push(parts[0]);
        } else if (parts.length === 2) {
          if (!current.includes(parts[0])) current.push(parts[0]);
        }
        setUnitListValue(current);
      } else {
        let updated = current.filter((c) => c !== code);
        if (parts.length === 2) {
          updated = updated.filter((item) => !item.startsWith(`${parts[0]}.${parts[1]}.`));
        } else if (parts.length === 1) {
          updated = updated.filter((item) => !item.startsWith(`${parts[0]}.`));
        }
        setUnitListValue(updated);
      }
    },
    [unitListValue, setUnitListValue],
  );

  const CheckboxNode: React.FC<{
    node: UnitNode;
    isHidden?: boolean;
    level?: number;
  }> = ({ node, isHidden = false, level = 0 }) => {
    const checked = unitListValue.includes(node.code);
    const filterFn = isHidden ? hasZeroInSubtree : showZero ? () => true : hasNonZeroInSubtree;

    const showChildrenByDefaultRoot =
      unitListValue.length === 0 &&
      defaultExpandedRootCode !== null &&
      node.code === defaultExpandedRootCode;
    const showChildNodes = Boolean(node.children && (checked || showChildrenByDefaultRoot));

    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', ml: level }}
        onClick={(e) => e.stopPropagation()}
      >
        <LabelWrapper onClick={(e) => e.stopPropagation()}>
          <StyledFormControlLabel
            label={node.title}
            control={
              <StyledCheckbox
                checked={checked}
                onChange={handleChange(node.code)}
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
            }
          />
          <StyledCountData>{node.count}</StyledCountData>
        </LabelWrapper>

        {showChildNodes && node.children && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {node.children.filter(filterFn).map((child) => (
              <Fragment key={`${child.code}-${child.count}${isHidden ? '-hidden' : ''}`}>
                <CheckboxNode node={child} isHidden={isHidden} level={level + 2} />
              </Fragment>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderGrouped = (groups: UnitNode[][], isHidden = false) =>
    groups
      .filter((g) => g.length > 0)
      .map((group, i) => {
        const filteredGroup =
          !isHidden && !showOtherCountries
            ? group.filter((land) => order.flat().includes(land.code))
            : group;

        if (filteredGroup.length === 0) return null;

        return (
          <Box
            key={`${isHidden ? 'zero' : 'nonzero'}-group-${i}`}
            sx={{ '&:not(:first-of-type)': { mt: 2 } }}
          >
            {filteredGroup.map((land) => (
              <Fragment key={`${land.code}-${land.count}${isHidden ? '-hidden' : ''}`}>
                <CheckboxNode node={land} isHidden={isHidden} level={0} />
              </Fragment>
            ))}
          </Box>
        );
      });

  const renderContent = () => (
    <>
      {renderGrouped(groupedNonZero, false)}

      {zeroList.length > 0 && (
        <>
          {nonZeroList.length > 0 && <Divider sx={{ my: 1 }} />}

          {!showZero ? (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowZero(true);
              }}
              sx={{
                fontSize: 12,
                color: COLORS['primary/600'],
                height: 'fit-content',
                width: '100%',
                textAlign: 'center',
                px: 0,
                textTransform: 'initial',
              }}
            >
              {t('filters.showEmpty')}
            </Button>
          ) : (
            <>
              {renderGrouped(groupedZero, true)}

              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowZero(false);
                }}
                sx={{
                  fontSize: 12,
                  color: COLORS['primary/600'],
                  height: 'fit-content',
                  width: '100%',
                  textAlign: 'center',
                  px: 0,
                  textTransform: 'initial',
                }}
              >
                {t('filters.hideEmpty')}
              </Button>
            </>
          )}
        </>
      )}

      <Button
        size="small"
        variant="text"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowOtherCountries((prev) => !prev);
        }}
        sx={{
          fontSize: 12,
          color: COLORS['secondary/500'],
          height: 'fit-content',
          width: '100%',
          textAlign: 'center',
          px: 0,
          textTransform: 'initial',
          '&:hover': {
            background: COLORS['secondary/50'],
          },
        }}
      >
        {t(showOtherCountries ? 'filters.hideCountries' : 'filters.showAllCountries')}
      </Button>
    </>
  );

  if (isSelect) {
    return (
      <FormControl size="small" sx={{ width }}>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          open={selectOpen}
          onOpen={() => setSelectOpen(true)}
          onClose={() => setSelectOpen(false)}
          label={label}
          value={unitListValue}
          onChange={() => {}}
          IconComponent={(props) => <ArrowDown {...props} />}
          renderValue={(selected) =>
            (selected as string[])
              .map((s) => getTitleByCode(s))
              .filter(Boolean)
              .join(', ')
          }
          MenuProps={{
            MenuListProps: { sx: { py: 0 } },
            PaperProps: { style: { maxHeight: 300, width: 250, overflowY: 'auto' } },
          }}
        >
          <Box
            sx={{ p: '4px 18px' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {renderContent()}
          </Box>
        </Select>
      </FormControl>
    );
  }

  return (
    <FilterAccordion title={label} id={label}>
      <StyledFormControl
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {renderContent()}
      </StyledFormControl>
    </FilterAccordion>
  );
};
